<template>
  <transition mode="out-in" name="fade" v-if="slider">
    <div v-show="!loading" :class="classes.transitionWrapper">
      <component :is="slider">
        <CardSelector
          v-for="(market, i) in cards"
          :key="i"
          :cardType="market[0]"
          :marketIds="market[1]"
        />
      </component>
    </div>
  </transition>
  <transition mode="out-in" name="fade" v-else-if="cards.length">
    <div
      v-show="!loading"
      :class="classes.transitionWrapper"
      :style="{ overflow: 'hidden' }"
    >
      <CardSelector :cardType="cards[0][0]" :marketIds="cards[0][1]" />
    </div>
  </transition>
</template>

<script lang="ts">
import CardSelector from "./CardSelector.vue";
import {
  computed,
  defineAsyncComponent,
  defineComponent,
  ref,
  watchEffect
} from "vue";
import { isTouchDevice } from "../lib/isTouchDevice";
import { provideCarouselContext } from "../lib/provideInjectors";
import { useShouldDisplayOffer } from "../hooks/useDisplayOffer";
import {
  injectBetDataRef,
  insightIsEnabled
} from "@fresh8/ad-data-composition/src/betData";
import { createUseStyles } from "@fresh8/vue-jss/src";
import { injectDisplayEvents } from "../composition/displayEvents";
import { injectPreview } from "@fresh8/ad-data-composition/src/preview";
import {
  useTemplateSize,
  MOBILE_LEADERBOARD,
  LMLB
} from "@fresh8/ad-data-composition/src/useTemplateSize";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { CardTypes } from "@fresh8/types/src";

import {
  enabledParlaySizes,
  enabledInsightSizes,
  enabledH2HSizes
} from "@fresh8/types/src/featureFlags";
import { injectInsightTestingContext } from "@fresh8/ads/src/lib/provideInjectors";

const useStyles = createUseStyles({
  transitionWrapper: {
    flex: "1",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box"
  }
});

export default defineComponent({
  name: "Content",
  components: {
    CardSelector
  },
  setup() {
    const classes = useStyles();
    const { parlayMarkets, flattendOrderedEvents } = injectDisplayEvents();
    const isTouch = isTouchDevice();
    const isCarousel = ref(false);
    const itemsOnView = ref(0);
    const shouldDisplayOffer = useShouldDisplayOffer();
    const betDataContext = injectBetDataRef();
    const previewContext = injectPreview();
    const { size } = useTemplateSize();
    const brand = injectBrand();
    const insightContext = injectInsightTestingContext();

    provideCarouselContext(
      ref({
        isCarousel,
        itemsOnView
      })
    );

    const filteredEvents = computed(() => {
      const isEnabledSize = enabledInsightSizes.includes(size.value.name);
      const isPreview = previewContext?.preview.value.isPreview;

      // only display insights if enabled by the brand and supported
      // by the size of ad. Feature is still not fully rolled out.
      return flattendOrderedEvents.value.filter(eventTuple => {
        if (brand && eventTuple[0].includes("insight")) {
          if (!isEnabledSize) return false;
          if (
            brand.insights.value.enabledLeagues.length ||
            brand.insights.value.enabledSports?.length
          ) {
            if (isPreview) return true;

            // use first market Id of insight event tuple.
            return (
              insightIsEnabled(
                eventTuple[1][0],
                brand.insights.value.enabledLeagues,
                brand.insights.value.enabledSports
              ) && insightContext.value.insightRandomTesting
            );
          }
          return false;
        }

        return true;
      });
    });

    const useParlayCard = computed(
      () =>
        enabledParlaySizes.includes(size.value.name) &&
        brand?.adTemplate.value.parlayCard?.useParlayCard &&
        parlayMarkets.value.length === 3
    );

    const useH2hCard = computed(
      () =>
        enabledH2HSizes.includes(size.value.name) &&
        brand?.adTemplate.value.h2hCard?.useH2hCard
    );

    const cards = computed(() => {
      let cardArray: [CardTypes, string[]][] = [];
      if (shouldDisplayOffer.value) {
        cardArray.push(["offer", []]);

        // Can only display the offer card on its own on
        // mobile ads.
        if (
          size.value.name === MOBILE_LEADERBOARD.name ||
          size.value.name === LMLB.name
        ) {
          return cardArray;
        }
      }

      if (useParlayCard.value) {
        cardArray.push(["parlay", parlayMarkets.value]);
      }

      cardArray = cardArray.concat(filteredEvents.value);

      if (useH2hCard.value && cardArray.length > 1) {
        cardArray = cardArray.reduce(
          (acc: [CardTypes, string[]][], curr: [CardTypes, string[]]) => {
            acc.push(curr);
            if (curr[0] === "wdw") {
              const event =
                betDataContext?.betData.value?.events[
                  betDataContext?.betData.value.markets[curr[1][0]].eventId
                ];
              if (
                event &&
                event.sport === "Football" &&
                event.competitors.away?.form &&
                event.competitors.away?.form.length > 0 &&
                event.competitors.away?.statistics &&
                Object.keys(event.competitors.away?.statistics).length > 0 &&
                Object.values(event.competitors.away?.statistics).reduce(
                  (a: number, c: number) => a + c,
                  0
                ) > 0 &&
                event.competitors.home?.form &&
                event.competitors.home?.form.length > 0 &&
                event.competitors.home?.statistics &&
                Object.keys(event.competitors.home?.statistics).length > 0 &&
                Object.values(event.competitors.away?.statistics).reduce(
                  (a: number, c: number) => a + c,
                  0
                ) > 0
              ) {
                acc.push(["h2h", curr[1]]);
              }
            }
            return acc;
          },
          []
        );
      }
      return cardArray;
    });

    const slider = computed(() => {
      // Never display the slider on the mobile leaderboard and pro evo size
      if (
        size.value.name === MOBILE_LEADERBOARD.name ||
        size.value.name === LMLB.name
      )
        return false;

      if ((cards.value?.length || 0) > 1) {
        isCarousel.value = true;
        if (isTouch) {
          return defineAsyncComponent(() => import("./TouchSlider"));
        }
        return defineAsyncComponent(() => import("./Carousel"));
      }

      return false;
    });

    // If no markets and no offer then collapse the ad
    watchEffect(() => {
      if (
        (cards.value?.length || 0) === 0 &&
        !previewContext?.preview.value.isPreview
      ) {
        window.parent.postMessage({ type: "f8_failed_loading" }, "*");
        document.documentElement.style.height = "0px";
        document.documentElement.style.overflow = "hidden";
        document.body.style.height = "0px";
        document.body.style.overflow = "hidden";
      } else if (document.documentElement.style.overflow === "hidden") {
        document.documentElement.style.height = "";
        document.documentElement.style.overflow = "";
        document.body.style.height = "";
        document.body.style.overflow = "";
      }
    });

    return {
      classes,
      slider,
      cards,
      loading: computed(() => betDataContext?.loading.value)
    };
  }
});
</script>
